import { convertGetMonitorInfo, safeInteropRegister } from "../../api-polyfill/src/common/windowFunctions";
import deepmerge from "deepmerge";
export class WindowService {
    constructor(glue, logger, router) {
        this.glue = glue;
        this.logger = logger;
        this.router = router;
        this.heartbeats = {};
    }
    async getMonitorInfo(params, caller) {
        let data = null;
        if (params.windowId) {
            const glueWindow = this.glue.windows.findById(params.windowId);
            const glueDisplay = await glueWindow?.getDisplay();
            data = glueDisplay ? convertGetMonitorInfo(glueDisplay) : null;
        }
        else {
            const glueDisplays = await this.glue.displays.all();
            data = glueDisplays.map((d) => convertGetMonitorInfo(d));
        }
        return { data };
    }
    onWindowAdded(win) {
        win.onClose(() => {
            this.logger.debug("window-service - Window closed", {
                uuid: "uuid",
                name: win.id
            });
            this.router.transmit("LauncherService.WindowClosed", {
                uuid: "uuid",
                name: win.id
            });
        });
    }
    async startHeartbeatMonitor(manifest) {
        const config = deepmerge({
            min: 5000,
            max: 1800000,
            crashed: 90000,
            possiblyCrashed: 60000,
            notResponding: 60000
        }, manifest?.finsemble?.servicesConfig?.window?.heartbeatResponseTimeout ?? {});
        await this.glue.windows.onWindowRemoved((win) => {
            const key = `${win.name}:${win.id}`;
            delete this.heartbeats[key];
        });
        await this.glue.interop.register("Finsemble.heartbeat", (data) => {
            const item = this.heartbeats[data.windowName] ?? {
                errorSent: false,
                notRespondingSent: false,
                warningSent: false
            };
            this.heartbeats[data.windowName] = {
                ...item,
                lastHeartbeat: Date.now()
            };
        });
        const constrain = (x) => Math.min(config.max, Math.max(x, config.min));
        const crashed = constrain(config.crashed);
        const possiblyCrashed = constrain(config.possiblyCrashed);
        const notResponding = constrain(config.notResponding);
        const broadcastStatusChange = (id, status) => this.router.transmit("Finsemble.WindowService.HeartbeatTimeout", { type: status, window: id });
        setInterval(() => {
            const now = Date.now();
            Object.entries(this.heartbeats).forEach(([id, value]) => {
                const crashedState = now - value.lastHeartbeat > crashed && !value.errorSent;
                const possiblyCrashedState = now - value.lastHeartbeat > possiblyCrashed && !value.warningSent;
                const notRespondingState = now - value.lastHeartbeat > notResponding && !value.notRespondingSent;
                const nowRespondingState = now - value.lastHeartbeat <= notResponding && (value.errorSent || value.warningSent || value.notRespondingSent);
                if (crashedState) {
                    const notifyCrashed = (crashedWindow) => () => {
                        if (Date.now() - crashedWindow.lastHeartbeat > crashed && !crashedWindow.errorSent) {
                            crashedWindow.errorSent = true;
                            broadcastStatusChange(id, "crashed");
                            this.logger.error("heartbeats - Heartbeat Status: Crashed Window", id);
                        }
                    };
                    setTimeout(notifyCrashed(value), 50);
                }
                else if (possiblyCrashedState) {
                    value.warningSent = true;
                    broadcastStatusChange(id, "possiblyCrashed");
                    this.logger.warn("heartbeats - Heartbeat Status: Possibly Crashed Window", id);
                }
                else if (notRespondingState) {
                    value.notRespondingSent = true;
                    broadcastStatusChange(id, "notResponding");
                    this.logger.warn("heartbeats - Heartbeat Status: Unresponsive Window", id);
                }
                else if (nowRespondingState) {
                    this.logger.info("heartbeats - Heartbeat Status: Window has returned to a responsive state", id);
                    broadcastStatusChange(id, "nowResponding");
                    value.warningSent = false;
                    value.errorSent = false;
                    value.notRespondingSent = false;
                }
            });
        }, 1000);
    }
    async start({ manifest }) {
        await new Promise(sleep => setTimeout(sleep, 5));
        await this.startHeartbeatMonitor(manifest);
        await this.router.publish("DockingService.AutoArrangeStatus", { isAutoArranged: {
                device0: false
            }
        });
        await this.glue.windows.onWindowAdded((win) => this.onWindowAdded(win));
        await this.glue.interop.register("FSBL.Clients.WindowClient.getMonitorInfo", safeInteropRegister(async (params, caller) => this.getMonitorInfo(params, caller), this.logger));
        this.router.addListener("DockingService.dockWindow", () => {
            const toolbar = this.glue.windows.find("Toolbar");
            if (toolbar) {
                toolbar.dock({
                    position: "top",
                    claimScreenArea: true
                });
            }
        });
    }
}
